import { dateAdd, parseDateString, toDateString } from "./utils";
const cached = {
    DE: new Map(),
    AT: new Map(),
};
export function getHolidayForDate(date, opts) {
    if (!(date instanceof Date)) {
        date = parseDateString(date);
    }
    if (!date) {
        return null;
    }
    return getHoliday(date.getFullYear(), date.getMonth(), date.getDate(), opts);
}
export function getHoliday(year, month, date, { holidays, country, state }) {
    const holiday = getHolidaysInYear(year, country).get(toDateString(new Date(year, month, date)));
    if (!holiday)
        return null;
    if (holidays && !holidays.includes(holiday.name))
        return null;
    if (state && !holiday.states.includes(state))
        return null;
    return holiday;
}
export function getHolidaysInYear(year, country = "DE") {
    if (!cached[country].has(year)) {
        const holidays = Object.values(HOLIDAYS[country])
            .filter((t) => {
            if (!t.since)
                return true;
            // holiday is valid if state is not listed in since or since is less than or equal to year
            return t.states.some((state) => !(state in t.since) || t.since[state] <= year);
        })
            .map(({ name, states, date, special, noRealHoliday, since }) => ({
            name,
            states: since ? states.filter((s) => !(s in since) || since[s] <= year) : states,
            date: date(year),
            special,
            noRealHoliday,
        }));
        cached[country].set(year, new Map(holidays.map((holiday) => [holiday.date, holiday])));
    }
    return cached[country].get(year);
}
export function getHolidaysInRange(from, to, options) {
    const fromDate = parseDateString(from);
    const toDate = parseDateString(to);
    const holidays = [];
    for (let year = fromDate.getFullYear(); year <= toDate.getFullYear(); year++) {
        holidays.push(...[...getHolidaysInYear(year, options.country).values()].filter((h) => !h.noRealHoliday &&
            (!options.holidays || options.holidays.includes(h.name)) &&
            (!options.state || h.states.includes(options.state)) &&
            h.date >= from &&
            h.date < to));
    }
    return holidays;
}
export function isHoliday(year, month, date, options) {
    const holiday = getHoliday(year, month, date, options);
    return holiday && !holiday.noRealHoliday;
}
/**
 * Calculate date of easter sunday through "Gaussche Osterformel"
 * See https://de.wikipedia.org/wiki/Gau%C3%9Fsche_Osterformel
 */
function getEasterSunday(year) {
    const a = year % 19;
    const d = (19 * a + 24) % 30;
    let day = d + ((2 * (year % 4) + 4 * (year % 7) + 6 * d + 5) % 7);
    if (day == 35 || (day == 34 && d == 28 && a > 10)) {
        day -= 7;
    }
    return toDateString(new Date(year, 2, 22 + day));
}
function getRelativeHoliday(year, offset) {
    const easterSunday = getEasterSunday(year);
    return dateAdd(easterSunday, { days: offset });
}
function getPrayerDay(year) {
    const date = new Date(year, 10, 16);
    const offset = (10 - date.getDay()) % 7;
    date.setDate(date.getDate() + offset);
    return toDateString(date);
}
export const HOLIDAYS = {
    DE: [
        {
            date: (year) => `${year}-01-01`,
            name: "Neujahr",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
        },
        {
            date: (year) => `${year}-01-06`,
            name: "Heilige 3 Könige",
            states: ["BW", "BY", "ST", "AB"],
        },
        {
            date: (year) => `${year}-03-08`,
            name: "Internationaler Frauentag",
            states: ["BE", "MV"],
            since: { BE: 2019, MV: 2023 },
        },
        {
            date: (year) => getRelativeHoliday(year, -2),
            name: "Karfreitag",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
        },
        {
            date: (year) => getEasterSunday(year),
            name: "Ostersonntag",
            states: [],
        },
        {
            date: (year) => getRelativeHoliday(year, 1),
            name: "Ostermontag",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
        },
        {
            date: (year) => `${year}-05-01`,
            name: "Tag der Arbeit",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
            special: true,
        },
        {
            date: (year) => getRelativeHoliday(year, 39),
            name: "Christi Himmelfahrt",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
        },
        {
            date: (year) => getRelativeHoliday(year, 49),
            name: "Pfingstsonntag",
            states: [],
        },
        {
            date: (year) => getRelativeHoliday(year, 50),
            name: "Pfingstmontag",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
        },
        {
            date: (year) => getRelativeHoliday(year, 60),
            name: "Fronleichnam",
            states: ["BW", "BY", "HE", "NW", "RP", "TH", "AB"],
        },
        {
            date: (year) => `${year}-08-08`,
            name: "Friedenstag",
            states: ["AB"],
        },
        {
            date: (year) => `${year}-08-15`,
            name: "Mariä Himmelfahrt",
            states: ["BY", "SL"],
        },
        {
            date: (year) => `${year}-09-20`,
            name: "Weltkindertag",
            states: ["TH"],
            since: { TH: 2019 },
        },
        {
            date: (year) => `${year}-10-03`,
            name: "Tag der Dt. Einheit",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
        },
        {
            date: (year) => `${year}-10-31`,
            name: "Reformationstag",
            states: ["BB", "MV", "NI", "SN", "ST", "SH", "TH", "HB", "HH"],
        },
        {
            date: (year) => `${year}-11-01`,
            name: "Allerheiligen",
            states: ["BW", "BY", "NW", "RP", "SL", "AB"],
        },
        {
            date: (year) => getPrayerDay(year),
            name: "Buß- und Bettag",
            states: ["SN"],
        },
        {
            date: (year) => `${year}-12-24`,
            name: "Heilig Abend",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
            noRealHoliday: true,
        },
        {
            date: (year) => `${year}-12-25`,
            name: "1. Weihnachtsfeiertag",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
            special: true,
        },
        {
            date: (year) => `${year}-12-26`,
            name: "2. Weihnachtsfeiertag",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
            special: true,
        },
        {
            date: (year) => `${year}-12-31`,
            name: "31. Dezember",
            states: [
                "BW",
                "BY",
                "BE",
                "BB",
                "HB",
                "HH",
                "HE",
                "MV",
                "NI",
                "NW",
                "RP",
                "SL",
                "SN",
                "ST",
                "SH",
                "TH",
                "AB",
            ],
            noRealHoliday: true,
        },
    ],
    AT: [
        {
            date: (year) => `${year}-01-01`,
            name: "Neujahr",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => `${year}-01-06`,
            name: "Heilige Drei Könige",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => getRelativeHoliday(year, 1),
            name: "Ostermontag",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => `${year}-05-01`,
            name: "Staatsfeiertag",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => getRelativeHoliday(year, 39),
            name: "Christi Himmelfahrt",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => getRelativeHoliday(year, 50),
            name: "Pfingstmontag",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => getRelativeHoliday(year, 60),
            name: "Fronleichnam",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => `${year}-08-15`,
            name: "Maria Himmelfahrt",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => `${year}-10-26`,
            name: "Nationalfeiertag",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => `${year}-11-01`,
            name: "Allerheiligen",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => `${year}-12-08`,
            name: "Maria Empfängnis",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => `${year}-12-25`,
            name: "Christtag",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        {
            date: (year) => `${year}-12-26`,
            name: "Stefanitag",
            states: ["AT-1", "AT-2", "AT-3", "AT-4", "AT-5", "AT-6", "AT-7", "AT-8", "AT-9"],
        },
        // Regional holidays
        {
            date: (year) => `${year}-03-19`,
            name: "Josefstag",
            states: ["AT-2", "AT-6", "AT-7", "AT-8"],
        },
        {
            date: (year) => `${year}-09-24`,
            name: "Rupertitag",
            states: ["AT-5"],
        },
        {
            date: (year) => `${year}-10-10`,
            name: "Tag der Volksabstimmung",
            states: ["AT-2"],
        },
        {
            date: (year) => `${year}-11-11`,
            name: "Martinstag",
            states: ["AT-1"],
        },
        {
            date: (year) => `${year}-11-15`,
            name: "Leopolditag",
            states: ["AT-3", "AT-9"],
        },
    ],
};
//# sourceMappingURL=holidays.js.map